import { callFlags as callFlagsHook } from '@src/hooks/call/useCallFlags';
import {
  ChatStatus,
  EventType,
  MediaType,
  MessagingProvider,
} from '@src/model/frontendmodel';
import callsApi from '@src/reducers/callsAPI';
import { chatByIdSelector } from '@src/selectors/chats';
// eslint-disable-next-line import/no-cycle
import store from '@src/store/reduxstore'; //TODO: fix circular dependency from agentchatmanager
import { type Call } from '@src/types/calls';
import type { ChatLastMessage, ChatTray } from '@src/types/chats';
import getContactPhoneNumber from '@src/utils/contact';
import { removePhoneNumberPrefix } from '@src/utils/formatPhoneNumber';
import { getOnceChatById } from '../chatManager';
import { callEventsBus } from './callEventsBus';
import setupCallEventsListener from './callEventsListener';

type CallEvent = {
  type: keyof typeof EventType;
  payload: CallEventPayload;
  timestamp: number;
  isMessageFromBot?: boolean;
};

type CallEventPayload = {
  from: string;
  to: string;
  mediaType: MediaType;
  messageFromBot: boolean;
  messageId: string;
  messagingProvider: MessagingProvider;
  providerMessageId: string;
  sendingTime: number;
  call: Call;
  assignedTo: ChatTray['assignedTo'];
  status?: keyof typeof ChatStatus;
  lastMessage?: ChatLastMessage;
};

type CanCallQuery = ReturnType<
  ReturnType<typeof callsApi.endpoints.getCanCall.initiate>
>;

const BSUID_PATTERN = /^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]{1,128}$/;

const callEventsRegister = async (
  agentChatId: string,
  { payload, timestamp }: CallEvent
) => {
  const call = payload?.call;
  if (!call?.id) return;

  // Every call event says what happened to it here: one that stops at a guard below
  // leaves the call bar where it was, and without a trace it cannot be told from one
  // that never arrived (ENG-2208). Ids only, no contact data.
  const trace = { callId: call.id, status: call.status, chatId: agentChatId };
  // eslint-disable-next-line no-console
  console.info('[callEvents] received', trace);

  try {
    const state = store.getState();

    const callFlags = callFlagsHook(call);

    let chat = chatByIdSelector(agentChatId)(state);
    if (!chat?.id) chat = await getOnceChatById(agentChatId);

    const lineId = chat?.viaBotAccountId;
    if (!lineId) {
      // eslint-disable-next-line no-console
      console.warn('[callEvents] dropped: the chat has no line', trace);
      return;
    }

    // Subscribed for the duration of the read: with subscribe:false, RTK Query keeps no
    // cache entry for a line nothing on screen has cached yet (a new chat, a fresh
    // reload), so the read resolved at once as 'uninitialized' and every event of the
    // call was dropped (ENG-2208). Released right after; the answer stays cached for
    // the endpoint's keepUnusedDataFor. The cast names what the thunk returns, since
    // the store's dispatch is typed without thunks.
    const canCallQuery = store.dispatch(
      callsApi.endpoints.getCanCall.initiate({ lineId })
    ) as unknown as CanCallQuery;
    let canCallResult: Awaited<CanCallQuery>;
    try {
      canCallResult = await canCallQuery;
    } finally {
      canCallQuery.unsubscribe();
    }
    if (!canCallResult.data?.canCall) {
      // eslint-disable-next-line no-console
      console.warn('[callEvents] dropped: the line cannot call', {
        ...trace,
        lineId,
        canCallStatus: canCallResult.status,
      });
      return;
    }

    const callePhone = removePhoneNumberPrefix(chat.viaBotAccountAlias);

    const agent = {
      phoneNumber: callePhone,
      whatsappPhoneNumberId: chat.viaBotAccount,
    };

    const contactCmaId = chat.contact.cmaId ?? '';
    const isBsuid = BSUID_PATTERN.test(contactCmaId);
    const user = {
      phoneNumber: isBsuid
        ? removePhoneNumberPrefix(chat.contact.mobileNumber ?? '')
        : getContactPhoneNumber(chat.contact),
      name: chat.contact.fullName,
      ...(isBsuid && { bsuid: contactCmaId }),
    };

    callEventsBus.emit(call.status, {
      id: call.id,
      chatId: agentChatId,
      status: call?.status,
      event: call?.event,
      direction: call?.direction,
      session: call.session,
      caller: callFlags.isCallOutgoing ? agent : user,
      callee: callFlags.isCallOutgoing ? user : agent,
      startedAt: timestamp,
    } as Call);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[callEvents] dropped: failed to register', trace, err);
  }
};

setupCallEventsListener();

export default callEventsRegister;
